import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './SessionManager.css';

const SessionManager = () => {
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchAccountStatus();
  }, []);

  const fetchAccountStatus = async () => {
    if (!token) return;

    try {
      const response = await axios.get(
        'https://bank-converter-app-3.onrender.com/account-status',
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAccountStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch account status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutDevice = async () => {
    if (!window.confirm("Are you sure you want to logout from this device?")) {
      return;
    }

    try {
      await axios.post(
        'https://bank-converter-app-3.onrender.com/logout',
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      localStorage.removeItem("token");
      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
      alert('Failed to logout. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeviceIcon = (deviceInfo) => {
    if (!deviceInfo) return '🖥️';
    const info = deviceInfo.toLowerCase();
    if (info.includes('mobile') || info.includes('android') || info.includes('iphone')) {
      return '📱';
    }
    if (info.includes('tablet') || info.includes('ipad')) {
      return '📱';
    }
    return '🖥️';
  };

  const truncateDeviceInfo = (info) => {
    if (!info) return 'Unknown Device';
    if (info.length > 60) {
      return info.substring(0, 60) + '...';
    }
    return info;
  };

  if (loading) {
    return <div className="session-loading">Loading active sessions...</div>;
  }

  if (!accountStatus) {
    return <div className="session-error">Failed to load session information</div>;
  }

  const { sessions, active_devices, max_devices, trial_status, is_premium } = accountStatus;

  return (
    <div className="session-manager">
      <div className="session-header">
        <h2>Active Devices & Sessions</h2>
        <p className="session-subtitle">
          Manage your active login sessions across devices
        </p>
      </div>

      <div className="session-stats">
        <div className="stat-card">
          <div className="stat-icon">📱</div>
          <div className="stat-content">
            <span className="stat-value">{active_devices} / {max_devices}</span>
            <span className="stat-label">Active Devices</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">{is_premium ? '👑' : '⏰'}</div>
          <div className="stat-content">
            <span className="stat-value">
              {is_premium ? 'Premium' : `${trial_status.days_remaining} days`}
            </span>
            <span className="stat-label">
              {is_premium ? 'Account Status' : 'Trial Remaining'}
            </span>
          </div>
        </div>
      </div>

      {active_devices >= max_devices && !is_premium && (
        <div className="session-warning">
          <span className="warning-icon">⚠️</span>
          <span>
            You've reached the maximum device limit.
            <strong> Upgrade to Premium</strong> for unlimited devices.
          </span>
        </div>
      )}

      <div className="session-list">
        <h3>Active Sessions ({sessions.length})</h3>
        {sessions.length === 0 ? (
          <p className="no-sessions">No active sessions found</p>
        ) : (
          sessions.map((session, index) => (
            <div key={index} className="session-card">
              <div className="session-icon">
                {getDeviceIcon(session.device_info)}
              </div>
              <div className="session-details">
                <div className="session-device">
                  {truncateDeviceInfo(session.device_info)}
                </div>
                <div className="session-time">
                  <span>Login: {formatDate(session.login_time)}</span>
                  <span className="separator">•</span>
                  <span>Last active: {formatDate(session.last_activity)}</span>
                </div>
              </div>
              {index === 0 && (
                <div className="session-badge">Current Device</div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="session-actions">
        <button className="btn-logout-current" onClick={handleLogoutDevice}>
          Logout from This Device
        </button>
        <p className="session-note">
          💡 <strong>Note:</strong> To logout from other devices, you'll need to login
          from that device and logout manually, or they will expire after 24 hours of inactivity.
        </p>
      </div>

      {!is_premium && (
        <div className="upgrade-section">
          <h3>🚀 Upgrade to Premium</h3>
          <ul className="premium-features">
            <li>✅ Unlimited active devices</li>
            <li>✅ Unlimited document uploads</li>
            <li>✅ Priority processing</li>
            <li>✅ Advanced export options</li>
            <li>✅ API access (coming soon)</li>
          </ul>
          <button className="btn-upgrade"
           onClick={() => (window.location.href = "#/settings?tab=billing")}
           >Upgrade Now -£15/month</button>
        </div>
      )}
    </div>
  );
};

export default SessionManager;